import { sort } from 'ramda';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { AsyncStatus } from '@/packages/typesafe-result';

import { useReduxDispatch } from '../../../../store';
import { selectHighways, selectHighwaysStatus } from '../../../../store/slices/highways/selectors/selectHighways';
import { selectCurrentLocationIdSafe } from '../../../../store/slices/location/selectors/selectCurrentLocaitonId';
import { requestHighways } from '../../../../store/thunks/highways';
import { type IHighwayDirection } from '../../../../types/highways';

export function useCurrentLocationHighways(): {
  status: AsyncStatus;
  data: IHighwayDirection[][];
  request: () => Promise<IHighwayDirection[]>;
} {
  const dispatch = useReduxDispatch();
  const highways = useSelector(selectHighways);
  const highwaysStatus = useSelector(selectHighwaysStatus);
  const regionId = useSelector(selectCurrentLocationIdSafe);

  const highwayDirections = useMemo(() => {
    function getDirectionsSize(highwayDirection: IHighwayDirection[]): number {
      // Высота заголовка относительно детей
      const REL_TITLE_HEIGHT = 2.5;

      // Размер колонки с учётом количества шоссе и названия направления
      return highwayDirection.reduce((prev, curr) => {
        return prev + curr.highways.length + REL_TITLE_HEIGHT;
      }, 0);
    }

    function getColumnSize(): number {
      const COLUMNS_COUNT = 4;
      const allDirectionsSize = getDirectionsSize(highways);

      return Math.round(allDirectionsSize / COLUMNS_COUNT);
    }

    const sortedDirections = sort<IHighwayDirection>((a, b) => {
      return b.highways.length - a.highways.length;
    })(highways);

    const columnSize = getColumnSize();
    const columns: IHighwayDirection[][] = [];

    sortedDirections.forEach(highwayDirection => {
      const index = columns.findIndex(column => {
        const currentColumnSize = getDirectionsSize(column);

        return currentColumnSize + highwayDirection.highways.length <= columnSize;
      });

      if (index >= 0) {
        columns[index].push(highwayDirection);
      } else {
        columns.push([highwayDirection]);
      }
    });

    return columns;
  }, [highways]);

  const request = useCallback(() => {
    return dispatch(requestHighways(regionId)).unwrap();
  }, [dispatch, regionId]);

  return useMemo(
    () => ({
      status: highwaysStatus,
      data: highwayDirections,
      request,
    }),
    [highwaysStatus, highwayDirections, request],
  );
}
